import { useState, useEffect, useCallback } from 'react';
import type { AdminStaffRole } from '@lighthouse/shared';
import { ADMIN_STAFF_ROLES } from '@lighthouse/shared';
import { adminApi } from '../services/api';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface UserRow {
  id: string;
  email: string;
  phone: string;
  role: string;
  adminStaffRole: AdminStaffRole | null;
  isLocked: boolean;
  lockedReason: string | null;
  mfaEnabled: boolean;
  createdAt: string;
}

type PendingUserAction =
  | { type: 'reset'; user: UserRow }
  | { type: 'delete'; user: UserRow }
  | null;

interface UserListResponse {
  data: UserRow[];
  nextCursor: string | null;
  hasMore: boolean;
}

const ROLES = ['client', 'therapist', 'corporate_hr', 'admin'] as const;

export function UsersPage() {
  const { hasPermission } = useAdminPermissions();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [lockedFilter, setLockedFilter] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingUserAction>(null);
  const [staffForm, setStaffForm] = useState({
    email: '',
    phone: '',
    password: '',
    adminStaffRole: 'support' as AdminStaffRole,
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setActionError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (roleFilter) params.set('role', roleFilter);
      if (lockedFilter) params.set('locked', lockedFilter);
      const qs = params.toString();
      const result = await adminApi<UserListResponse>(`/admin/users${qs ? `?${qs}` : ''}`);
      setUsers(result.data);
    } catch (err) {
      setActionError((err as Error).message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, lockedFilter]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleLock = async (user: UserRow) => {
    const reason = window.prompt('Lock reason (required):');
    if (!reason || reason.length < 3) return;
    try {
      await adminApi(`/admin/users/${user.id}/lock`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      setActionMessage(`Locked ${user.email}`);
      void loadUsers();
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  const handleUnlock = async (user: UserRow) => {
    try {
      await adminApi(`/admin/users/${user.id}/unlock`, { method: 'POST' });
      setActionMessage(`Unlocked ${user.email}`);
      void loadUsers();
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  const handleResetPassword = (user: UserRow) => {
    setPendingAction({ type: 'reset', user });
  };

  const handleDelete = (user: UserRow) => {
    setPendingAction({ type: 'delete', user });
  };

  const runPendingAction = async () => {
    if (!pendingAction) return;
    const { type, user } = pendingAction;
    setPendingAction(null);
    try {
      if (type === 'reset') {
        const result = await adminApi<{ temporaryPassword?: string }>(
          `/admin/users/${user.id}/reset-password`,
          { method: 'POST', body: JSON.stringify({}) }
        );
        if (result.temporaryPassword) {
          setActionMessage(`Temporary password for ${user.email}: ${result.temporaryPassword}`);
        } else {
          setActionMessage(`Password reset for ${user.email}`);
        }
      } else {
        await adminApi(`/admin/users/${user.id}`, { method: 'DELETE' });
        setActionMessage(`Deleted ${user.email}`);
        void loadUsers();
      }
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  const handleRoleChange = async (user: UserRow, role: string) => {
    try {
      await adminApi(`/admin/users/${user.id}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
      setActionMessage(`Updated role for ${user.email}`);
      void loadUsers();
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  const handleStaffRoleChange = async (user: UserRow, adminStaffRole: AdminStaffRole) => {
    try {
      await adminApi(`/admin/users/${user.id}/admin-role`, {
        method: 'PATCH',
        body: JSON.stringify({ adminStaffRole }),
      });
      setActionMessage(`Updated staff role for ${user.email}`);
      void loadUsers();
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  const handleCreateStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminApi('/admin/users/staff', {
        method: 'POST',
        body: JSON.stringify(staffForm),
      });
      setActionMessage(`Created admin staff: ${staffForm.email}`);
      setShowCreateStaff(false);
      setStaffForm({ email: '', phone: '', password: '', adminStaffRole: 'support' });
      void loadUsers();
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        {hasPermission('admins:manage') && (
          <button onClick={() => setShowCreateStaff(true)} className="btn-primary">
            Add Admin Staff
          </button>
        )}
      </div>

      {actionError && (
        <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{actionError}</div>
      )}
      {actionMessage && (
        <div className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-700">
          {actionMessage}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Search email or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field w-64"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="input-field w-40"
        >
          <option value="">All roles</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          value={lockedFilter}
          onChange={(e) => setLockedFilter(e.target.value)}
          className="input-field w-40"
        >
          <option value="">All statuses</option>
          <option value="true">Locked</option>
          <option value="false">Active</option>
        </select>
        <button onClick={() => void loadUsers()} className="btn-secondary">
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="mt-8 text-center text-gray-500">Loading...</div>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-gray-600">
                <th className="pb-3 pr-4 font-medium">Email</th>
                <th className="pb-3 pr-4 font-medium">Role</th>
                <th className="pb-3 pr-4 font-medium">Staff Role</th>
                <th className="pb-3 pr-4 font-medium">Status</th>
                <th className="pb-3 pr-4 font-medium">MFA</th>
                <th className="pb-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-gray-100">
                  <td className="py-3 pr-4">
                    <div className="font-medium">{user.email}</div>
                    <div className="text-xs text-gray-500">{user.phone}</div>
                  </td>
                  <td className="py-3 pr-4">
                    {hasPermission('users:update_role') ? (
                      <select
                        value={user.role}
                        onChange={(e) => void handleRoleChange(user, e.target.value)}
                        className="input-field text-xs"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      user.role
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    {user.role === 'admin' && hasPermission('admins:manage') ? (
                      <select
                        value={user.adminStaffRole ?? 'support'}
                        onChange={(e) =>
                          void handleStaffRoleChange(user, e.target.value as AdminStaffRole)
                        }
                        className="input-field text-xs"
                      >
                        {ADMIN_STAFF_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      (user.adminStaffRole ?? '—')
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    {user.isLocked ? (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                        Locked
                      </span>
                    ) : (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        Active
                      </span>
                    )}
                    {user.lockedReason && (
                      <div className="mt-1 text-xs text-gray-500">{user.lockedReason}</div>
                    )}
                  </td>
                  <td className="py-3 pr-4">{user.mfaEnabled ? 'On' : 'Off'}</td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-2">
                      {user.isLocked
                        ? hasPermission('users:unlock') && (
                            <button
                              onClick={() => void handleUnlock(user)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Unlock
                            </button>
                          )
                        : hasPermission('users:lock') && (
                            <button
                              onClick={() => void handleLock(user)}
                              className="text-xs text-orange-600 hover:underline"
                            >
                              Lock
                            </button>
                          )}
                      {hasPermission('users:reset_password') && (
                        <button
                          onClick={() => void handleResetPassword(user)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Reset PW
                        </button>
                      )}
                      {hasPermission('users:delete') && (
                        <button
                          onClick={() => void handleDelete(user)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <p className="mt-4 text-center text-gray-500">No users found</p>}
        </div>
      )}

      {showCreateStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <form onSubmit={handleCreateStaff} className="card w-full max-w-md">
            <h2 className="text-lg font-bold">Create Admin Staff</h2>
            <div className="mt-4 space-y-3">
              <input
                type="email"
                placeholder="Email"
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                className="input-field"
                required
              />
              <input
                type="tel"
                placeholder="Phone"
                value={staffForm.phone}
                onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                className="input-field"
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={staffForm.password}
                onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                className="input-field"
                required
                minLength={8}
              />
              <select
                value={staffForm.adminStaffRole}
                onChange={(e) =>
                  setStaffForm({ ...staffForm, adminStaffRole: e.target.value as AdminStaffRole })
                }
                className="input-field"
              >
                {ADMIN_STAFF_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex gap-2">
              <button type="submit" className="btn-primary flex-1">
                Create
              </button>
              <button
                type="button"
                onClick={() => setShowCreateStaff(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      <ConfirmDialog
        open={pendingAction !== null}
        title={
          pendingAction?.type === 'reset'
            ? 'Reset password?'
            : pendingAction?.type === 'delete'
              ? 'Delete user?'
              : 'Confirm'
        }
        message={
          pendingAction?.type === 'reset'
            ? `Reset password for ${pendingAction.user.email}? A temporary password will be generated.`
            : pendingAction?.type === 'delete'
              ? `Soft-delete ${pendingAction.user.email}? This cannot be undone easily.`
              : ''
        }
        confirmLabel={pendingAction?.type === 'delete' ? 'Delete' : 'Reset password'}
        danger={pendingAction?.type === 'delete'}
        onCancel={() => setPendingAction(null)}
        onConfirm={runPendingAction}
      />
    </div>
  );
}
